import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Lock, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) return <Navigate to="/admin/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(" "));
      else setError("Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src="https://customer-assets.emergentagent.com/job_importacao-expert/artifacts/gz8vakfs_viamundi_logo_sem_fundo_cores_originais.png" alt="Viamundi" className="h-16 mx-auto mb-4 object-contain" />
          <p className="text-muted-foreground text-sm">Painel Administrativo</p>
        </div>

        <form
          data-testid="admin-login-form"
          onSubmit={handleSubmit}
          className="space-y-5 bg-card border border-border/50 rounded-2xl p-8"
        >
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="h-5 w-5 text-primary" />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="text-destructive text-sm text-center bg-destructive/10 p-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
            <Input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-2 bg-background/50 border-border/50 rounded-none px-4 py-5 focus:border-primary"
              placeholder="admin@viamundi.com"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Senha</Label>
            <Input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-2 bg-background/50 border-border/50 rounded-none px-4 py-5 focus:border-primary"
              placeholder="Sua senha"
            />
          </div>

          <Button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full rounded-none py-6 font-bold uppercase tracking-widest text-sm bg-primary text-primary-foreground glow-cyan hover:scale-[1.02] transition-transform"
          >
            {loading ? "Entrando..." : "Entrar"}
          </Button>
        </form>

        <div className="text-center mt-6">
          <a
            href="/"
            data-testid="back-to-site"
            className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft className="h-3 w-3" /> Voltar ao site
          </a>
        </div>
      </div>
    </div>
  );
}
